// src/components/deal-detail/AcceptanceEmailMissingFields.jsx
// Inline mini-form for the fields not yet on the deal that a given
// acceptance email needs. The form is honest about what it can and
// can't fix in place:
//
//   - Fields that live on `deal.acceptanceEmails.{inspection,lender,
//     otherSideTitle}` get inline inputs here.
//   - Fields that live on the deal record (agentName, propertyAddress,
//     deadlines, commission rates) or on the client contacts (names,
//     recipient emails) or in Settings (our title company / closer,
//     broker admin fee default) are listed below the inline section
//     with a friendly label and a "where to fix" hint, so the user
//     isn't left wondering why "Add Missing Info (3)" expanded into an
//     empty form.

import { useState } from "react";

// Map every required-template field to where the user can actually
// edit it. `inline` means the form below has inputs for it; otherwise
// `location` + `hint` tell the user where the value really lives.
const FIELD_INFO = {
  // Inline-fixable — these write into deal.acceptanceEmails.{section}.
  inspectionDate: { label: "Inspection Date", inline: "inspection" },
  walkthroughTime: { label: "Walk-Through Time", inline: "inspection" },
  inspectorName: { label: "Inspector Name", inline: "inspection" },
  lenderName: { label: "Lender Name", inline: "lender" },
  lenderPronounSubject: { label: "Lender Pronoun", inline: "lender" },
  otherSideTitleCompany: {
    label: "Other-Side Title Company",
    inline: "otherSideTitle",
  },
  otherSideTitleCloserName: {
    label: "Other-Side Title Closer Name",
    inline: "otherSideTitle",
  },

  // Deal-level — open the deal in edit mode to set.
  agentName: {
    label: "Agent on the deal",
    location: "deal",
    hint: "Open the deal in edit mode and pick an agent.",
  },
  agentFirstName: {
    label: "Agent on the deal",
    location: "deal",
    hint: "Open the deal in edit mode and pick an agent.",
  },
  propertyAddress: {
    label: "Property Address",
    location: "deal",
    hint: "Set the property address on the deal.",
  },
  inspectionContingencyEnd: {
    label: "Inspection Contingency End",
    location: "deal",
    hint: "Set the Inspection Deadline on the deal (Listing Details → Under Contract).",
  },
  totalCommissionPct: {
    label: "Total Commission %",
    location: "deal",
    hint: "Set the commission rate on the deal.",
  },
  buyerSideCommissionPct: {
    label: "Buyer-Side Commission %",
    location: "deal",
    hint: "Set the buyer-side commission rate on the deal.",
  },

  // Contact-level — fix on the client(s) attached to the deal.
  sellerNames: {
    label: "Seller name(s)",
    location: "contact",
    hint: "Add the seller(s) as clients on this deal.",
  },
  buyerNames: {
    label: "Buyer name(s)",
    location: "contact",
    hint: "Add the buyer(s) as clients on this deal.",
  },
  clientNames: {
    label: "Client name(s)",
    location: "contact",
    hint: "Add the client(s) to this deal.",
  },

  // Settings-level — Team-wide defaults.
  ourTitleCompany: {
    label: "Our Title Company",
    location: "settings",
    hint: "Settings → Title Defaults.",
  },
  ourTitleCloserName: {
    label: "Our Title Closer",
    location: "settings",
    hint: "Settings → Title Defaults.",
  },
  ourTitleCloserFirstName: {
    label: "Our Title Closer",
    location: "settings",
    hint: "Settings → Title Defaults.",
  },
  brokerAdminFee: {
    label: "Broker Admin Fee",
    location: "settings",
    hint: "Settings → Commission Defaults (or override on the deal).",
  },
};

const LOCATION_LABEL = {
  deal: "On the deal",
  contact: "On the client contact",
  settings: "In Settings",
  unknown: "Elsewhere",
};

// Best-effort human label for unmapped fields — turn camelCase into
// "Camel Case" so we never show a raw key to the user.
function prettyField(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

export default function AcceptanceEmailMissingFields({
  templateId,
  missingFields = [],
  current,
  onSave,
  onCancel,
  saving,
}) {
  const initial = current || {};
  const [inspection, setInspection] = useState(
    initial.inspection || { name: "", date: "", walkthroughTime: "" },
  );
  const [lender, setLender] = useState(
    initial.lender || { name: "", pronoun: "" },
  );
  const [otherSideTitle, setOtherSideTitle] = useState(
    initial.otherSideTitle || {
      company: "",
      closerName: "",
      email: "",
      phone: "",
    },
  );

  // Group the missing fields by where they live so we can render the
  // right inline inputs and list the rest under "Fix these elsewhere".
  const inlineMissing = new Set();
  const elsewhereMissing = [];
  for (const field of missingFields) {
    const info = FIELD_INFO[field];
    if (info?.inline) {
      inlineMissing.add(info.inline);
    } else {
      elsewhereMissing.push({
        field,
        label: info?.label || prettyField(field),
        location: info?.location || "unknown",
        hint: info?.hint,
      });
    }
  }

  const showInspection = inlineMissing.has("inspection");
  const showLender = inlineMissing.has("lender");
  const showOtherSideTitle = inlineMissing.has("otherSideTitle");
  const hasInlineSection = showInspection || showLender || showOtherSideTitle;

  const submit = () => {
    const patch = {};
    if (showInspection) patch.inspection = inspection;
    if (showLender) patch.lender = lender;
    if (showOtherSideTitle) patch.otherSideTitle = otherSideTitle;
    onSave(patch);
  };

  // Group "fix elsewhere" items by location for cleaner copy.
  const groupedElsewhere = elsewhereMissing.reduce((acc, m) => {
    (acc[m.location] = acc[m.location] || []).push(m);
    return acc;
  }, {});
  const locationOrder = ["deal", "contact", "settings", "unknown"];

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mt-2 space-y-3">
      {showInspection && (
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Inspector Name
            </label>
            <input
              type="text"
              value={inspection.name}
              onChange={(e) =>
                setInspection({ ...inspection, name: e.target.value })
              }
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Inspection Date
            </label>
            <input
              type="date"
              value={inspection.date}
              onChange={(e) =>
                setInspection({ ...inspection, date: e.target.value })
              }
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Walk-Through Time
            </label>
            <input
              type="text"
              placeholder="e.g. 10:00 AM"
              value={inspection.walkthroughTime}
              onChange={(e) =>
                setInspection({
                  ...inspection,
                  walkthroughTime: e.target.value,
                })
              }
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}

      {showLender && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Lender Name
            </label>
            <input
              type="text"
              value={lender.name}
              onChange={(e) =>
                setLender({ ...lender, name: e.target.value })
              }
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Lender Pronoun
            </label>
            <select
              value={lender.pronoun}
              onChange={(e) =>
                setLender({ ...lender, pronoun: e.target.value })
              }
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Select…</option>
              <option value="he">He / Him</option>
              <option value="she">She / Her</option>
            </select>
          </div>
        </div>
      )}

      {showOtherSideTitle && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Other-Side Title Company
              </label>
              <input
                type="text"
                value={otherSideTitle.company}
                onChange={(e) =>
                  setOtherSideTitle({
                    ...otherSideTitle,
                    company: e.target.value,
                  })
                }
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Other-Side Closer Name
              </label>
              <input
                type="text"
                value={otherSideTitle.closerName}
                onChange={(e) =>
                  setOtherSideTitle({
                    ...otherSideTitle,
                    closerName: e.target.value,
                  })
                }
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Other-Side Closer Email
              </label>
              <input
                type="email"
                value={otherSideTitle.email}
                onChange={(e) =>
                  setOtherSideTitle({
                    ...otherSideTitle,
                    email: e.target.value,
                  })
                }
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Other-Side Closer Phone
              </label>
              <input
                type="tel"
                value={otherSideTitle.phone}
                onChange={(e) =>
                  setOtherSideTitle({
                    ...otherSideTitle,
                    phone: e.target.value,
                  })
                }
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {elsewhereMissing.length > 0 && (
        <div
          className={
            hasInlineSection ? "border-t border-slate-200 pt-3" : ""
          }
        >
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            {hasInlineSection
              ? "Also still needed (fix where shown)"
              : "Needed before this email can send"}
          </div>
          <ul className="space-y-2">
            {locationOrder
              .filter((loc) => groupedElsewhere[loc])
              .map((loc) => (
                <li key={loc} className="text-xs">
                  <div className="font-semibold text-slate-700">
                    {LOCATION_LABEL[loc]}
                  </div>
                  <ul className="mt-1 space-y-1 pl-3">
                    {groupedElsewhere[loc].map((m) => (
                      <li key={m.field} className="text-slate-600">
                        <span className="font-medium text-slate-800">
                          {m.label}
                        </span>
                        {m.hint && (
                          <span className="text-slate-500"> — {m.hint}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-white disabled:opacity-50"
        >
          {hasInlineSection ? "Cancel" : "Close"}
        </button>
        {hasInlineSection && (
          <button
            onClick={submit}
            disabled={saving}
            className="px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-800 hover:bg-slate-900 text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
}
